import { useState } from 'react';
import {
  Dialog,
  Input,
  Select,
  SelectOption,
  TovButton,
  TovField,
  msg,
  useToast,
} from '@tov-plus/ui';
import type { GalleryUploadDialogProps } from './GalleryUploadDialog';
import { useGalleryUploadQueue } from './useGalleryUploadQueue';

export function GalleryQueuedUploadDialog(props: GalleryUploadDialogProps) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState('');
  const [targetId, setTargetId] = useState(
    props.uploadOptions[0]?.albumId ?? props.uploadOptions[0]?.eventId ?? '',
  );
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const queue = useGalleryUploadQueue({
    ...props,
    enabled: props.uploadConstraints.resumableEnabled,
  });
  const target = props.uploadOptions.find(
    (option) => (option.albumId ?? option.eventId) === targetId,
  );
  async function enqueue() {
    if (!target || !files.length) return;
    if (
      files.some(
        (file) =>
          !props.uploadConstraints.acceptedMimePrefixes.some((prefix) =>
            file.type.startsWith(prefix),
          ) ||
          file.size > props.uploadConstraints.maxFileSizeBytes ||
          file.size === 0,
      )
    ) {
      toast.error(
        msg('pages.gallery_page.selected_file_larger_than_upload_limit'),
      );
      return;
    }
    setSaving(true);
    try {
      await queue.enqueue(files, target, caption);
      setFiles([]);
      setCaption('');
      toast.success(
        msg(
          'pages.gallery_upload_queue.files_saved_in_this_browser_waiting_for_connection',
        ),
      );
    } catch {
      toast.error(
        msg(
          'pages.gallery_upload_queue.could_not_save_files_check_available_browser_storage',
        ),
      );
    } finally {
      setSaving(false);
    }
  }
  async function action(run: () => Promise<void>) {
    try {
      await run();
    } catch {
      toast.error(
        msg('pages.gallery_upload_queue.could_not_update_upload_queue'),
      );
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={msg('pages.gallery_page.gallery_add_photos')}
      trigger={
        <TovButton>
          {msg('pages.gallery_page.gallery_add_photos')}
          {queue.uploads.length ? ` (${queue.uploads.length})` : ''}
        </TovButton>
      }
      footer={
        <TovButton
          disabled={saving || !files.length || !target}
          onClick={() => {
            void enqueue();
          }}
        >
          {msg('pages.gallery_upload_queue.save_files_to_upload_queue')}
        </TovButton>
      }
    >
      <div className="gallery-upload-form" data-testid="gallery-upload-queue">
        <TovField label={msg('general.modules.gallery.name')}>
          <Select
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
          >
            {props.uploadOptions.map((option) => (
              <SelectOption
                key={option.albumId ?? option.eventId}
                value={option.albumId ?? option.eventId}
              >
                {option.displayName}
              </SelectOption>
            ))}
          </Select>
        </TovField>
        <TovField
          label={msg('pages.gallery_page.pages_gallery_page_photo_or_video')}
        >
          <Input
            type="file"
            multiple
            accept={props.uploadConstraints.acceptedMimePrefixes
              .map((prefix) => (prefix.endsWith('/') ? `${prefix}*` : prefix))
              .join(',')}
            onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
          />
        </TovField>
        <TovField label={msg('pages.gallery_page.pages_gallery_page_caption')}>
          <Input
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
          />
        </TovField>
        <p>
          {msg(
            'pages.gallery_upload_queue.keep_this_page_open_to_upload_or_return_here_to_resume',
          )}
        </p>
        <p>
          {msg(
            'pages.gallery_upload_queue.queued_files_stay_in_this_browser_but_your_device_may_clear_browser_storage',
          )}
        </p>
        <p>
          {msg(
            'pages.gallery_upload_queue.up_to_100_files_and_250_mb_can_be_saved_locally',
          )}
        </p>
        <ul
          className="gallery-upload-queue"
          aria-label={msg('pages.gallery_upload_queue.upload_queue')}
        >
          {queue.uploads.map((upload) => (
            <li key={upload.id}>
              <span>{upload.fileName}</span>
              <progress
                max={100}
                value={upload.progress}
                aria-label={msg(
                  'pages.gallery_upload_queue.file_upload_progress',
                )}
              />
              <span>{upload.progress}%</span>
              {upload.status === 'paused' || upload.status === 'failed' ? (
                <TovButton
                  onClick={() => {
                    void action(() => queue.setStatus(upload.id, 'queued'));
                  }}
                >
                  {msg('pages.gallery_upload_queue.resume_upload')}
                </TovButton>
              ) : (
                <TovButton
                  onClick={() => {
                    void action(() => queue.setStatus(upload.id, 'paused'));
                  }}
                >
                  {msg('pages.gallery_upload_queue.pause_upload')}
                </TovButton>
              )}
              <TovButton
                onClick={() => {
                  void action(() => queue.remove(upload.id));
                }}
              >
                {msg('pages.gallery_upload_queue.remove_queued_file')}
              </TovButton>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
